import axios from 'axios';
import merge from 'lodash/merge';
import { toastr } from 'react-redux-toastr';

import APPConf from './config/common';

// Если нужно переключиться на режим, когда при входе в админку на Проде каждый раз спрашивается пароль,
// то раскоментируй строку ниже
// const storage = process.env.NODE_ENV === 'production' ? sessionStorage : localStorage;
const storage = localStorage;

export const decodeToken = (token) => {
  try {
    if (token.split('.').length !== 3 || typeof token !== 'string') {
      return null;
    }

    const payload = token.split('.')[1];
    const base64 = payload.replace('-', '+').replace('_', '/');

    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

export const isTokenExpired = (token) => {
  const { exp: expirationUTCSeconds } = decodeToken(token) || {};

  if (!expirationUTCSeconds) {
    return true;
  }

  const expirationDate = new Date(0);

  expirationDate.setUTCSeconds(expirationUTCSeconds);

  return expirationDate.valueOf() < new Date().valueOf();
};

export const getTokenExpirationTimeout = (token) => {
  const { exp: expirationUTCSeconds } = decodeToken(token) || {};

  const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  const timeoutMs = expirationUTCSeconds
    ? (expirationUTCSeconds * 1000) - new Date().valueOf()
    : 0;

  // setTimeout принимает только 32битные числа,
  // потому ограничим таймаут истечения срока токена в 1 неделю.
  // в данном случае этого достаточно, так как предполагается,
  // что пользователь вряд ли будет держать открытой страницу в браузере более этого срока
  return timeoutMs < oneWeekMs ? timeoutMs : oneWeekMs;
};

export const saveCredentials = (credentials) => {
  storage.setItem('APP_Auth', JSON.stringify(credentials));
};

export const getCredentials = () => {
  return JSON.parse(storage.getItem('APP_Auth')) || {};
};

export const clearCredentials = () => {
  storage.removeItem('APP_Auth');
  sessionStorage.removeItem(WSNT_TOKEN_KEY);
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event('auth:logout'));
  }
};

// Далее функции внедрены для socket-соединений
/**
 * Генерирует уникальный ВАЛИДНЫЙ UUID для текущей вкладки.
 * Используется как deviceId при подключении к WebSocket.
 * 
 * @returns {string | null} Валидный UUID или null, если невозможно сгенерировать
 */
export const getUniqueDeviceIdForTab = () => {
  const TAB_ID_KEY = 'socket_tab_device_id'; // ← новое имя ключа
  let deviceId = sessionStorage.getItem(TAB_ID_KEY);

  if (!deviceId) {
    try {
      // Генерируем настоящий UUID v4
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        deviceId = crypto.randomUUID();
      } else {
        // Fallback для старых браузеров (редко нужно в 2026)
        deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.random() * 16 | 0;
          const v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }
      sessionStorage.setItem(TAB_ID_KEY, deviceId);
    } catch {
      // console.error('Failed to generate unique device ID', e);
      return null;
    }
  }

  return deviceId;
};

const WSNT_TOKEN_KEY = 'wsnt_token';

export const saveWsntToken = (token) => {
  sessionStorage.setItem(WSNT_TOKEN_KEY, token);
};

export const getWsntToken = () => {
  return sessionStorage.getItem(WSNT_TOKEN_KEY);
};

export const clearWsntToken = () => {
  sessionStorage.removeItem(WSNT_TOKEN_KEY);
};
// Конец функций для socket-соединений

const DEFAULT_API_BASE = APPConf.serverAddress;

// Кэш axios-инстансов по baseURL. Основной инстанс (без явного apiBase)
// дублируется в window.axiosAPI ради обратной совместимости.
const getAxiosCache = () => window.axiosAPICache || (window.axiosAPICache = Object.create(null));

export function createAxios(baseURL = DEFAULT_API_BASE) {
  if (baseURL === DEFAULT_API_BASE && window.axiosAPI) {
    return window.axiosAPI;
  }

  const cache = getAxiosCache();
  if (cache[baseURL]) {
    return cache[baseURL];
  }

  const ax = axios.create({
    baseURL,
    timeout: 60000,
    validateStatus: status => {
      return status >= 200 && status <= 503; // ok
    },
  });

  // Подавление TOAST-уведомлений об ошибках для конкретного запроса.
  // Включается передачей { silentToast: true } в requestConfig вызова makeRequest —
  // axios прокидывает это поле в config и оно доступно здесь как response.config / error.config.
  const isSilent = (config) => Boolean(config?.silentToast);

  ax.interceptors.response.use(response => { // соединение установлено, есть какой-то ответ бэка
    if (response.status === 430) { // logout
      clearCredentials();

      return response;
    }

    // пока отобразим все 500е ошибы всплывающим сообщением
    // @todo legacy не уверен, что это вообще необходимо
    if (response.status >= 500 && !isSilent(response.config)) {
      toastr.error('Ошибка: ', `${ response.data.message } ( ${ response.status } )`);
    }

    if (response.status >= 400 && response.status !== 401) {
      return { ...response, error: response.error || { response } };
    }

    return response;
  }, error => { // не удалось установить соединение
    if (axios.isCancel(error)) {
      return { error, connectError: true, data: {} };
    }

    if (!isSilent(error.config)) {
      const msg = String.prototype.concat(APPConf.connectivityError, (process.env.DEBUG ? ' [createAxios]' : ''));

      toastr.error('Ошибка: ', msg);
    }

    if (error.code === 'ECONNABORTED') {
      return { error: { code: error.code, message: error.message }, connectError: true, data: {} };
    }

    return { error, connectError: true, data: {} };
  });

  cache[baseURL] = ax;
  if (baseURL === DEFAULT_API_BASE) {
    window.axiosAPI = ax;
  }

  return ax;
}

/**
 * Make request to API
 *
 * @param {String} method HTTP method - `get`, `post`, `put`, `patch` etc.
 * @param {String} url The server URL that will be used for the request
 * @param {Object|String} requestData The data to be sent as the request body.
 *                                    Only applicable for request methods 'PUT', 'POST', and 'PATCH'
 * @param {Object} requestConfig Axios request config. See {@link https://github.com/axios/axios#request-config}
 * @param okStatus
 * @return {Promise<any>}
 * @see https://github.com/axios/axios#axios-api
 */
export const makeRequest = (method, url, requestData = {}, requestConfig = {}, okStatus = 200) => {
  const ax = createAxios(requestConfig.apiBase || DEFAULT_API_BASE);

  return new Promise((resolve, reject) => {
    const auth = getCredentials();
    const headers = (requestData instanceof FormData)
      ? {
        'Content-Type': 'multipart/form-data',
      }
      : {
        'Content-Type': 'application/json',
      };

    // По умолчанию токен авторизации добавляется во все запросы.
    // Опция `skipAuth: true` в requestConfig отключает добавление токена —
    // используется для публичных эндпоинтов (восстановление пароля и т.п.),
    // куда бек не должен получать authorization-заголовок.
    if (!requestConfig.skipAuth) {
      headers.Authorization = auth?.token;
    }

    const config = merge({
      method,
      headers,
      url,
      data: requestData,
    }, requestConfig);

    return ax(config).then(response => {
      const {
        connectError = false,
        status,
        data = {},
        error = {},
      } = response;

      const isError = status !== okStatus;

      let errorData = error?.error || error?.response?.data || data?.error || (error.toJSON && error.toJSON());

      // @todo убрать этот костыль после того, как апи стандартизирует формат ошибок
      if (typeof errorData === 'string') {
        errorData = { message: errorData };
      }

      // @todo временное? решение для преобразование API ошибки валидации
      // из формата { field: "fieldName", message: "validationErrorMessage" }
      // в формат понимаемый используемой либой валидации
      if (errorData && (errorData.field && errorData.message)) {
        errorData = { [errorData.field]: errorData.message };
      }

      resolve({
        status,
        headers: response.headers,
        connectError, // ошибка сети
        error: isError, // ошибка (статус ответа не тот который нужен)
        errorData, // содержимое ошибки
        data, // данные
      });
    }).catch((error) => {
      reject(error);

      // eslint-disable-next-line no-console
      console.error('axios request error', error);
    });
  });
};
